import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Plus, 
  Search, 
  Trash2,
  Bell,
  Users,
  UserCheck,
  Send
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificationsPage = ({ isAdmin = true }) => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    target_type: 'all'
  });

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Error al cargar notificaciones');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/notifications`, formData);
      toast.success(t('savedSuccessfully'));
      setShowCreateModal(false);
      resetForm();
      fetchNotifications();
    } catch (error) {
      console.error('Error creating notification:', error);
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/notifications/${selectedNotification.id}`);
      toast.success(t('deletedSuccessfully'));
      setShowDeleteDialog(false);
      setSelectedNotification(null);
      fetchNotifications();
    } catch (error) {
      console.error('Error deleting notification:', error);
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`);
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const resetForm = () => {
    setFormData({ title: '', message: '', target_type: 'all' });
  };

  const getTargetBadge = (targetType) => {
    const config = {
      all: { color: 'bg-[#467322]/10 text-[#467322] border-[#467322]/20', icon: Users, label: t('all') },
      admins: { color: 'bg-purple-100 text-purple-700 border-purple-200', icon: Users, label: t('admins') },
      distributors: { color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Users, label: t('distributors') },
      clients: { color: 'bg-green-100 text-green-700 border-green-200', icon: UserCheck, label: t('clients') },
    };
    const c = config[targetType] || config.all;
    return (
      <Badge className={`${c.color} flex items-center gap-1`}>
        <c.icon size={12} />
        {c.label}
      </Badge>
    );
  };

  const filteredNotifications = notifications.filter(n =>
    n.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    n.message.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#467322] border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="notifications-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-[#467322]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {t('notifications')}
          </h1>
          <p className="text-gray-600 mt-1">Centro de notificaciones</p>
        </div>
        {isAdmin && (
          <Button 
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="bg-[#467322] hover:bg-[#3A601C] text-white"
            data-testid="create-notification-btn"
          >
            <Plus size={20} className="mr-2" />
            {t('newNotification')}
          </Button>
        )}
      </div>

      {/* Search */}
      <Card className="bg-white border border-[#467322]/20">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#467322]" size={20} />
            <Input
              placeholder={`${t('search')}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 border-[#467322]/30 focus:border-[#467322]"
              data-testid="search-notifications"
            />
          </div>
        </CardContent>
      </Card>

      {/* Notifications List */}
      <div className="space-y-4">
        {filteredNotifications.length === 0 ? (
          <Card className="bg-white border border-[#467322]/20">
            <CardContent className="py-12">
              <div className="text-center text-gray-500">
                <Bell size={48} className="mx-auto mb-4 opacity-50" />
                <p>{t('noNotifications')}</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          filteredNotifications.map((notification) => {
            const isRead = notification.read_by?.includes(user?.id);
            return (
              <Card 
                key={notification.id}
                className={`bg-white border transition-all duration-200 hover:shadow-md cursor-pointer ${
                  isRead ? 'border-gray-200' : 'border-[#467322] border-l-4'
                }`}
                onClick={() => !isRead && markAsRead(notification.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <Bell size={18} className={isRead ? 'text-gray-400' : 'text-[#467322]'} />
                        <h3 className={`font-semibold ${isRead ? 'text-gray-600' : 'text-black'}`}>
                          {notification.title}
                        </h3>
                        {getTargetBadge(notification.target_type)}
                        {!isRead && (
                          <Badge className="bg-[#467322] text-white">Nuevo</Badge>
                        )}
                      </div>
                      <p className={`text-sm ${isRead ? 'text-gray-500' : 'text-gray-700'}`}>
                        {notification.message}
                      </p>
                      <p className="text-xs text-gray-400 mt-2">
                        {new Date(notification.created_at).toLocaleString()}
                      </p>
                    </div>
                    {isAdmin && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedNotification(notification);
                          setShowDeleteDialog(true);
                        }}
                        className="text-red-500 hover:bg-red-50"
                        data-testid={`delete-notification-${notification.id}`}
                      >
                        <Trash2 size={16} />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* Create Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="bg-white border border-[#467322]/20 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl text-[#467322]" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {t('newNotification')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-black">{t('title')} *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                className="border-[#467322]/30 focus:border-[#467322]"
                data-testid="input-notification-title"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-black">{t('message')} *</Label>
              <Textarea
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                rows={4}
                className="border-[#467322]/30 focus:border-[#467322]"
                data-testid="input-notification-message"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-black">{t('targetAudience')}</Label>
              <Select 
                value={formData.target_type} 
                onValueChange={(value) => setFormData({ ...formData, target_type: value })}
              >
                <SelectTrigger className="border-[#467322]/30">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white border border-[#467322]/20">
                  <SelectItem value="all">{t('all')}</SelectItem>
                  <SelectItem value="admins">{t('admins')}</SelectItem>
                  <SelectItem value="distributors">{t('distributors')}</SelectItem>
                  <SelectItem value="clients">{t('clients')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowCreateModal(false)}
                className="border-[#467322] text-[#467322]"
              >
                {t('cancel')}
              </Button>
              <Button
                type="submit"
                className="bg-[#467322] hover:bg-[#3A601C] text-white"
                data-testid="save-notification-btn"
              >
                <Send size={16} className="mr-2" />
                Enviar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-white border border-[#467322]/20">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#467322]">{t('confirmDelete')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteWarning')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#467322] text-[#467322]">
              {t('cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default NotificationsPage;
